import apiClient from "src/utils/apiClient";
import store from "src/store/store";
import { createToast } from "src/store/toastsSlice";
import { TOAST_TIMEOUT } from "src/config/Toast";
import { getAuditJobHistory } from "src/store/auditDataUploadSlice";
import { AuditData } from "src/store/auditSlice";
import {
  MetricsRequest,
  MetricsResponse,
} from "src/components/pages/audit/auditDashboard/metricsTypes";

export interface AuditWorkflowExtractionPayload {
  file: File;
  modelId: string;
}

export interface GetReviewedCasesCSVPayload {
  auditWorkflowId: string;
  modelId: string;
}

export interface ExportByIdsRequest {
  incident_ids?: string[];
  audit_workflow_ids?: string[];
}

export interface ExportByIdsResponse {
  success: boolean;
  message: string;
  record_count: number;
  filename: string;
}

class AuditWorkflowService {
  async submitAuditWorkflowExtraction(payload: AuditWorkflowExtractionPayload) {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("model_id", payload.modelId);
    const res = await apiClient.post("/audit_workflow/extract", formData);
    if (res) {
      store.dispatch(
        createToast({
          type: "success",
          message: "Audit job submit successfully.",
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    store.dispatch(getAuditJobHistory());
    return res;
  }

  async getAuditWorkflowHistory() {
    return await apiClient.get("/audit_workflow/history");
  }

  async getAuditDataListView(auditWorkflowId: string) {
    return await apiClient.get(
      `/audit_workflow/audit_data_list?audit_workflow_id=${auditWorkflowId}`,
    );
  }

  async getAuditJobById(auditWorkflowId: string) {
    return await apiClient.get(`/audit_workflow/${auditWorkflowId}`);
  }
  async getAuditData(payload: { auditWorkflowId: string; incidentId: string }) {
    return await apiClient.get(
      `/audit_workflow/audit_data/?audit_workflow_id=${payload.auditWorkflowId}&incident_id=${payload.incidentId}`,
    );
  }

  async updateAuditData(payload: AuditData) {
    const res = await apiClient.put(`/audit_workflow/update`, payload);
    if (res) {
      store.dispatch(
        createToast({
          type: "success",
          message: "Updated audit data successfully.",
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  async getReviewedCasesCSV(payload: GetReviewedCasesCSVPayload) {
    const res = await apiClient.post(
      `/audit_workflow/post_process?audit_workflow_id=${payload.auditWorkflowId}&model_id=${payload.modelId}`,
      {
        responseType: "blob",
      },
    );
    if (res) {
      store.dispatch(
        createToast({
          type: "success",
          message: "Download review case in csv format successfully.",
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  async getSmartAssistantData(incidentId: string) {
    return await apiClient.get(`/audit_workflow/smart_assistant_data/${incidentId}`);
  }

  async getFilteredAuditData(
    filters: any[],
    orderByClause?: string,
    limit?: number,
    offset?: number,
  ) {
    // Build query parameters
    const params = new URLSearchParams();

    if (orderByClause) {
      params.append("order_by_clause", orderByClause);
    }

    if (limit) {
      params.append("limit", limit.toString());
    }

    if (offset) {
      params.append("offset", offset.toString());
    }

    const url = `/audit_workflow/filtered_data${params.toString() ? "?" + params.toString() : ""}`;

    // Send filters as the request body directly
    return await apiClient.post(url, filters || []);
  }

  async getMetrics(request: MetricsRequest): Promise<MetricsResponse> {
    const response = await apiClient.post("/audit_workflow/metrics", request);
    return response.data;
  }

  async exportFilteredAuditData(filters: any[]) {
    const res = await apiClient.post("/audit_workflow/export_filtered_data", filters);
    if (res) {
      store.dispatch(
        createToast({
          type: "success",
          message:
            res.data.message ||
            "Export request submitted successfully. An email will be sent with the download link.",
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  async exportFilteredAuditDataByIds(request: ExportByIdsRequest): Promise<ExportByIdsResponse> {
    const res = await apiClient.post("/audit_workflow/export_filtered_data_by_ids", request);
    if (res) {
      store.dispatch(
        createToast({
          type: "success",
          message:
            res.data.message ||
            "Export request submitted successfully. An email will be sent with the download link.",
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res.data;
  }
}

export default new AuditWorkflowService();
